import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Image,
  Animated,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  IconButton,
  useTheme,
  Chip,
  ProgressBar,
  Avatar,
} from 'react-native-paper';
import {
  Users,
  MessageCircle,
  Clock,
  Star,
  X,
  ThumbsUp,
  Info,
  Calendar,
  Globe,
  BookOpen,
  Target,
  Zap,
  TrendingUp,
} from 'lucide-react-native';
import type {
  PeerMatch,
  CompatibilityScore,
} from '../../store/slices/peerMatchingSlice';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { logger } from '../../utils/logger';

interface PeerMatchingCardProps {
  match: PeerMatch;
  onConnect: (matchId: string, message?: string) => void;
  onDismiss: (matchId: string, reason?: string) => void;
  onViewProfile: (userId: string) => void;
  onShowCompatibilityDetails: (compatibility: CompatibilityScore) => void;
  showAnalytics?: boolean;
}

export function PeerMatchingCard({
  match,
  onConnect,
  onDismiss,
  onViewProfile,
  onShowCompatibilityDetails,
  showAnalytics = false,
}: PeerMatchingCardProps) {
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const [showAIReasoning, setShowAIReasoning] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const fadeAnim = useState(new Animated.Value(1))[0];

  const { peer, compatibility, aiReasoning, confidence, connectionStatus } =
    match;

  // Get compatibility color based on score
  const getCompatibilityColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4'; // High compatibility - Teal
    if (score >= 0.6) return '#FF6B35'; // Medium compatibility - Orange
    return '#E74C3C'; // Low compatibility - Red
  };

  const compatibilityColor = getCompatibilityColor(compatibility.overall);
  const compatibilityLabel =
    compatibility.overall >= 0.8
      ? 'Excellent Match'
      : compatibility.overall >= 0.6
        ? 'Good Match'
        : 'Fair Match';

  // Format time zone display
  const formatTimeZone = (timeZone: string) => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
      return formatter.format(now);
    } catch {
      return timeZone;
    }
  };

  // Get status color and icon
  const getStatusInfo = (status: typeof connectionStatus) => {
    switch (status) {
      case 'suggested':
        return { color: theme.colors.primary, icon: Users, label: 'New Match' };
      case 'pending':
        return { color: '#FF6B35', icon: Clock, label: 'Request Sent' };
      case 'connected':
        return { color: '#4ECDC4', icon: ThumbsUp, label: 'Connected' };
      case 'declined':
        return { color: '#E74C3C', icon: X, label: 'Declined' };
      default:
        return {
          color: theme.colors.onSurfaceVariant,
          icon: Users,
          label: 'Unknown',
        };
    }
  };

  const statusInfo = getStatusInfo(connectionStatus);
  const StatusIcon = statusInfo.icon;

  const handleConnect = useCallback(async () => {
    if (isConnecting) return;

    setIsConnecting(true);
    try {
      await onConnect(match.id, connectionMessage || undefined);

      // Animate card to show success
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 0.7,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    } catch (error) {
      logger.error('Failed to connect:', error);
    } finally {
      setIsConnecting(false);
    }
  }, [isConnecting, onConnect, match.id, connectionMessage, fadeAnim]);

  const handleDismiss = useCallback(
    (reason?: string) => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        onDismiss(match.id, reason);
      });
    },
    [fadeAnim, onDismiss, match.id]
  );

  const renderCompatibilityBreakdown = () => (
    <View style={styles.compatibilityBreakdown}>
      <Text
        variant='labelMedium'
        style={[styles.breakdownTitle, { color: theme.colors.onSurface }]}
      >
        Compatibility Breakdown
      </Text>

      <View style={styles.breakdownItem}>
        <View style={styles.breakdownLabel}>
          <Target size={14} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            Skill Level
          </Text>
        </View>
        <View style={styles.breakdownScore}>
          <ProgressBar
            progress={compatibility.skillAlignment}
            color={getCompatibilityColor(compatibility.skillAlignment)}
            style={styles.progressBar}
          />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {Math.round(compatibility.skillAlignment * 100)}%
          </Text>
        </View>
      </View>

      <View style={styles.breakdownItem}>
        <View style={styles.breakdownLabel}>
          <BookOpen size={14} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            Learning Goals
          </Text>
        </View>
        <View style={styles.breakdownScore}>
          <ProgressBar
            progress={compatibility.goalAlignment}
            color={getCompatibilityColor(compatibility.goalAlignment)}
            style={styles.progressBar}
          />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {Math.round(compatibility.goalAlignment * 100)}%
          </Text>
        </View>
      </View>

      <View style={styles.breakdownItem}>
        <View style={styles.breakdownLabel}>
          <Calendar size={14} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            Schedule
          </Text>
        </View>
        <View style={styles.breakdownScore}>
          <ProgressBar
            progress={compatibility.scheduleAlignment}
            color={getCompatibilityColor(compatibility.scheduleAlignment)}
            style={styles.progressBar}
          />
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {Math.round(compatibility.scheduleAlignment * 100)}%
          </Text>
        </View>
      </View>

      {compatibility.breakdown.commonMasteryAreas.length > 0 && (
        <View style={styles.commonAreas}>
          <Text
            variant='bodySmall'
            style={[
              styles.commonAreasTitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Common Interests:
          </Text>
          <View style={styles.areasContainer}>
            {compatibility.breakdown.commonMasteryAreas
              .slice(0, 3)
              .map((area, index) => (
                <Chip
                  key={index}
                  mode='outlined'
                  textStyle={styles.areaChipText}
                  style={[
                    styles.areaChip,
                    { borderColor: theme.colors.primary },
                  ]}
                >
                  {area.replace(/_/g, ' ')}
                </Chip>
              ))}
            {compatibility.breakdown.commonMasteryAreas.length > 3 && (
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                +{compatibility.breakdown.commonMasteryAreas.length - 3} more
              </Text>
            )}
          </View>
        </View>
      )}
    </View>
  );

  return (
    <Animated.View style={{ opacity: fadeAnim }}>
      <Card
        style={[styles.container, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <StatusIcon size={16} color={statusInfo.color} />
              <Text
                variant='labelMedium'
                style={[styles.statusLabel, { color: statusInfo.color }]}
              >
                {statusInfo.label}
              </Text>
              <Chip
                mode='outlined'
                textStyle={[
                  styles.compatibilityText,
                  { color: compatibilityColor },
                ]}
                style={[
                  styles.compatibilityChip,
                  { borderColor: compatibilityColor },
                ]}
              >
                {Math.round(compatibility.overall * 100)}% match
              </Chip>
            </View>

            <View style={styles.headerRight}>
              <ConfidenceIndicator
                confidence={confidence}
                size='small'
                showLabel={false}
              />
              {connectionStatus === 'suggested' && (
                <IconButton
                  icon={X}
                  size={20}
                  onPress={() => handleDismiss('not_interested')}
                  style={styles.dismissButton}
                />
              )}
            </View>
          </View>

          {/* Peer Profile */}
          <View style={styles.profileSection}>
            <TouchableOpacity
              style={styles.profileHeader}
              onPress={() => onViewProfile(peer.userId)}
            >
              <Avatar.Image
                size={60}
                source={{
                  uri: peer.avatarUrl || 'https://via.placeholder.com/60',
                }}
                style={styles.avatar}
              />
              <View style={styles.profileInfo}>
                <View style={styles.nameRow}>
                  <Text
                    variant='titleMedium'
                    style={[
                      styles.displayName,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {peer.displayName}
                  </Text>
                  {peer.isOnline && (
                    <View
                      style={[
                        styles.onlineIndicator,
                        { backgroundColor: '#4ECDC4' },
                      ]}
                    />
                  )}
                </View>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.username,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  @{peer.username}
                </Text>
                {peer.bio && (
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.bio,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                    numberOfLines={2}
                  >
                    {peer.bio}
                  </Text>
                )}
              </View>
            </TouchableOpacity>

            {/* Peer Details */}
            <View style={styles.peerDetails}>
              <View style={styles.detailRow}>
                <Globe size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  {formatTimeZone(peer.timeZone)}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Zap size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  Level {Math.round(peer.skillLevel * 10)}/10
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Clock size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={{ color: theme.colors.onSurfaceVariant }}
                >
                  Active {peer.lastActive}
                </Text>
              </View>
            </View>

            {/* Mastery Areas */}
            {peer.masteryAreas.length > 0 && (
              <View style={styles.masteryAreas}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.masteryTitle,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Expertise:
                </Text>
                <View style={styles.masteryTags}>
                  {peer.masteryAreas.slice(0, 3).map((area, index) => (
                    <Chip
                      key={index}
                      mode='outlined'
                      textStyle={styles.masteryChipText}
                      style={[
                        styles.masteryChip,
                        { borderColor: theme.colors.outline },
                      ]}
                    >
                      {area.replace(/_/g, ' ')}
                    </Chip>
                  ))}
                  {peer.masteryAreas.length > 3 && (
                    <Text
                      variant='bodySmall'
                      style={{ color: theme.colors.onSurfaceVariant }}
                    >
                      +{peer.masteryAreas.length - 3}
                    </Text>
                  )}
                </View>
              </View>
            )}
          </View>

          {/* AI Reasoning and Details */}
          <View style={styles.reasoningSection}>
            <View style={styles.toggleButtons}>
              <Button
                mode='text'
                onPress={() => setShowDetails(!showDetails)}
                style={styles.toggleButton}
                labelStyle={styles.toggleButtonLabel}
                icon={TrendingUp}
                compact
              >
                {showDetails ? 'Hide Details' : 'Match Details'}
              </Button>

              <Button
                mode='text'
                onPress={() => setShowAIReasoning(!showAIReasoning)}
                style={styles.toggleButton}
                labelStyle={styles.toggleButtonLabel}
                icon={Info}
                compact
              >
                {showAIReasoning ? 'Hide AI' : 'Why Match?'}
              </Button>
            </View>

            {showDetails && renderCompatibilityBreakdown()}

            {showAIReasoning && (
              <View style={styles.aiReasoning}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.reasoningText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {aiReasoning}
                </Text>
              </View>
            )}
          </View>

          {/* Actions */}
          {connectionStatus === 'suggested' && (
            <View style={styles.actions}>
              <View style={styles.dismissOptions}>
                <Button
                  mode='outlined'
                  onPress={() => handleDismiss('wrong_level')}
                  style={styles.dismissOptionButton}
                  labelStyle={styles.dismissButtonLabel}
                  compact
                >
                  Wrong Level
                </Button>
                <Button
                  mode='outlined'
                  onPress={() => handleDismiss('no_time')}
                  style={styles.dismissOptionButton}
                  labelStyle={styles.dismissButtonLabel}
                  compact
                >
                  No Time
                </Button>
                <Button
                  mode='outlined'
                  onPress={() => handleDismiss('not_interested')}
                  style={styles.dismissOptionButton}
                  labelStyle={styles.dismissButtonLabel}
                  compact
                >
                  Not Interested
                </Button>
              </View>

              <View style={styles.primaryActions}>
                <Button
                  mode='outlined'
                  onPress={() => onViewProfile(peer.userId)}
                  style={styles.actionButton}
                  labelStyle={styles.actionButtonLabel}
                  icon='account'
                  compact
                >
                  View Profile
                </Button>

                <Button
                  mode='contained'
                  onPress={handleConnect}
                  style={[styles.actionButton, styles.connectButton]}
                  labelStyle={styles.connectButtonLabel}
                  icon={MessageCircle}
                  loading={isConnecting}
                  disabled={isConnecting}
                  compact
                >
                  Connect
                </Button>
              </View>
            </View>
          )}

          {connectionStatus === 'pending' && (
            <View style={styles.pendingStatus}>
              <Text
                variant='bodySmall'
                style={[
                  styles.pendingText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Connection request sent. Waiting for response...
              </Text>
            </View>
          )}

          {connectionStatus === 'connected' && (
            <View style={styles.connectedStatus}>
              <Button
                mode='contained'
                onPress={() => {
                  /* Navigate to chat */
                }}
                style={styles.chatButton}
                labelStyle={styles.chatButtonLabel}
                icon={MessageCircle}
              >
                Start Conversation
              </Button>
            </View>
          )}
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  content: {
    paddingVertical: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusLabel: {
    fontWeight: '600',
    fontSize: 11,
    textTransform: 'uppercase',
  },
  compatibilityChip: {
    height: 24,
  },
  compatibilityText: {
    fontSize: 10,
    fontWeight: '600',
  },
  dismissButton: {
    margin: 0,
  },
  profileSection: {
    marginBottom: 16,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  avatar: {
    marginRight: 12,
  },
  profileInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  displayName: {
    fontWeight: '600',
  },
  onlineIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  username: {
    marginBottom: 4,
  },
  bio: {
    lineHeight: 16,
  },
  peerDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  masteryAreas: {
    marginBottom: 8,
  },
  masteryTitle: {
    marginBottom: 6,
    fontSize: 12,
  },
  masteryTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  masteryChip: {
    height: 20,
  },
  masteryChipText: {
    fontSize: 9,
    textTransform: 'capitalize',
  },
  reasoningSection: {
    marginBottom: 16,
  },
  toggleButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  toggleButton: {
    alignSelf: 'flex-start',
    marginLeft: -8,
  },
  toggleButtonLabel: {
    fontSize: 12,
  },
  compatibilityBreakdown: {
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  breakdownTitle: {
    marginBottom: 12,
    fontWeight: '600',
  },
  breakdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  breakdownLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  breakdownScore: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  progressBar: {
    flex: 1,
    height: 4,
  },
  commonAreas: {
    marginTop: 8,
  },
  commonAreasTitle: {
    marginBottom: 6,
    fontSize: 12,
  },
  areasContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  areaChip: {
    height: 20,
  },
  areaChipText: {
    fontSize: 9,
    textTransform: 'capitalize',
  },
  aiReasoning: {
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    padding: 12,
  },
  reasoningText: {
    lineHeight: 16,
    fontStyle: 'italic',
  },
  actions: {
    gap: 12,
  },
  dismissOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dismissOptionButton: {
    flex: 0,
    minWidth: 80,
  },
  dismissButtonLabel: {
    fontSize: 11,
  },
  primaryActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  actionButtonLabel: {
    fontSize: 12,
  },
  connectButton: {
    backgroundColor: '#4ECDC4',
  },
  connectButtonLabel: {
    fontSize: 12,
    color: 'white',
  },
  pendingStatus: {
    alignItems: 'center',
    padding: 12,
    backgroundColor: 'rgba(255, 107, 53, 0.1)',
    borderRadius: 8,
  },
  pendingText: {
    fontStyle: 'italic',
    textAlign: 'center',
  },
  connectedStatus: {
    alignItems: 'center',
  },
  chatButton: {
    backgroundColor: '#4ECDC4',
    minWidth: 200,
  },
  chatButtonLabel: {
    color: 'white',
  },
});
